<template>
  <div class="m-file-input-items">
    <div
      v-for="file of files"
      :key="attachmentUiRowKey(file)"
      :class="cardClasses(file)"
      :title="getAttachmentCardTitle(file)"
      @click.stop="onCardClick($event, file)"
    >
      <div v-if="hasSignatures(file)" class="signature-badge">
        <i class="pi pi-pencil"></i>
      </div>

      <div :class="getFileIconClass(file)" class="file-icon-box">
        <img
          v-if="getCardThumbUrl(file)"
          :src="getCardThumbUrl(file) || undefined"
          class="file-thumb"
          alt=""
        />
        <template v-else>
          <i :class="getFileIcon(file)" class="file-icon"></i>
          <span class="file-extension-badge">{{ fileExtension(file) }}</span>
        </template>
      </div>

      <div class="file-info">
        <div class="file-name">{{ getFileName(file) }}</div>
        <div class="file-meta">
          <span class="file-size">{{ formatSize(getFileSize(file)) }}</span>
        </div>
      </div>
      <AttachmentTapeSyncBadge :ui="tapeBadge.getBadgeUi(file)" />
    </div>
    <div
      v-if="showAddCard"
      class="file-item-card file-item-card-upload"
      @click.stop="emit('add-click')"
    >
      <i class="pi pi-plus" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { FileItem } from '@/common/types/fileItem'
import { computed } from 'vue'
import { attachmentUiRowKey } from '@/common/utils/fileAttachmentsUniData'
import AttachmentTapeSyncBadge from '@/components/ui/AttachmentTapeSyncBadge.vue'
import type { useAttachmentTapeBadge } from '@/common/composables/useAttachmentTapeBadge'
import { buildAttachmentPropertiesPlainText, parseAttachmentSignatures } from '@/common/utils/attachmentPropertiesTooltip'

const props = defineProps<{
  files: FileItem[]
  tapeBadge: ReturnType<typeof useAttachmentTapeBadge>
  getFileName: (file: FileItem) => string
  getFileSize: (file: FileItem) => number
  getCardThumbUrl: (file: FileItem) => string | null
  formatSize: (bytes: number) => string
  getFileIcon: (file: FileItem) => string
  getFileIconClass: (file: FileItem) => string
  fileExtension: (file: FileItem) => string
  isFileSelected: (file: FileItem) => boolean
  isFileMultiSelected: (file: FileItem) => boolean
  getSyncCardClasses: (file: FileItem) => { clickBlocked: boolean; busy: boolean }
  showAddCard?: boolean
}>()

const showAddCard = computed(() => props.showAddCard !== false)

const emit = defineEmits<{
  'card-click': [event: MouseEvent | undefined, file: FileItem]
  'add-click': []
}>()

function hasSignatures(file: FileItem): boolean {
  return parseAttachmentSignatures(file).length > 0
}

function getAttachmentCardTitle(file: FileItem): string {
  return buildAttachmentPropertiesPlainText(file)
}

function cardClasses(file: FileItem): Array<string | Record<string, boolean>> {
  const sync = props.getSyncCardClasses(file)
  return [
    'file-item-card',
    {
      'file-item-card--selected': props.isFileMultiSelected(file),
      'file-item-card--selected-preview': props.isFileSelected(file),
      'file-item-card--blocked': sync.clickBlocked,
      'file-item-card--busy': sync.busy,
    },
  ]
}

function onCardClick(event: MouseEvent | undefined, file: FileItem): void {
  if (props.getSyncCardClasses(file).clickBlocked) return
  emit('card-click', event, file)
}
</script>

<style scoped lang="scss">
.m-file-input-items {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 0;
}

.file-item-card {
  background: #fff;
  color: #374151;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 150px;
  min-height: 175px;
  padding: 1.25rem 0.75rem;
  transition:
    transform 0.2s,
    box-shadow 0.2s,
    border-color 0.2s,
    background-color 0.2s;
  position: relative;
  cursor: pointer;

  &-upload {
    justify-content: center;
    background: rgb(173, 173, 173);
    i {
      font-size: 2rem;
      font-weight: bold;
      color: #e6e6e6;
    }
  }
  &--selected {
    border: 2px solid var(--primary-color, #c6c6c6);
    box-shadow: 0 6px 20px rgba(210, 210, 210, 0.5);
    transform: scale(1.01);
  }
  &--blocked {
    cursor: not-allowed;
    opacity: 0.85;

    &:hover {
      box-shadow: none;
      transform: none;
    }
  }

  &--selected-preview {
    border: 2px solid var(--primary-color, #c6c6c6);
    box-shadow: 0 6px 20px color-mix(in srgb, var(--primary-color, #c6c6c6) 35%, transparent);
    transform: scale(1.01);

    .file-name {
      color: var(--primary-color, #374151);
      font-weight: 700;
      transition: color 0.2s;
    }
  }
}

.file-item-card:hover {
  box-shadow: var(--shadow-4);
  transform: translateY(-1px);
}

.signature-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--primary-color, #1e3a6e);
  box-shadow: none;

  i {
    font-size: 0.75rem;
    color: #fff;
  }
}

.file-icon-box {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 80px;
  height: 80px;
  border-radius: var(--border-radius);
  margin-bottom: 0.75rem;
  background: var(--surface-b);
}

.file-icon {
  font-size: 3rem;
  color: var(--surface-500);
}

.file-extension-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: bold;
}

.file-icon-box.excel .file-icon {
  color: #21a366;
}
.file-icon-box.word .file-icon {
  color: #2b579a;
}
.file-icon-box.pdf .file-icon {
  color: #e53935;
}

.file-info {
  margin-top: 8px;
  text-align: center;
  width: 100%;
}

.file-name {
  font-weight: 600;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.25rem;
}

.file-meta {
  font-size: 0.75rem;
}

.file-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--border-radius);
}
</style>
